/**
 * Initialize donut charts using Chart.js
 */
export function initDonutCharts() {
    const chartContainers = document.querySelectorAll('.js-donut-chart');
    
    if (chartContainers.length === 0) return;

    // Load Chart.js dynamically if not already loaded
    if (typeof Chart === 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
        script.onload = () => setupIntersectionObserver(chartContainers);
        document.head.appendChild(script);
    } else {
        setupIntersectionObserver(chartContainers);
    }
}

function setupIntersectionObserver(containers) {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !entry.target.dataset.initialized) {
                entry.target.dataset.initialized = 'true';
                
                const canvas = entry.target.querySelector('canvas');
                if (!canvas) return;

                const data = JSON.parse(entry.target.dataset.chartData || '{}');
                const title = entry.target.dataset.chartTitle || '';
                
                // Animate the chart
                createDonutChart(canvas, data, title);
                
                // Animate the legends
                animateLegends(entry.target);
            }
        });
    }, {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
    });

    containers.forEach(container => {
        observer.observe(container);
    });
}

function animateLegends(chartContainer) {
    // Find the legend items (they're siblings to the chart container)
    const chartCard = chartContainer.closest('.chart-card');
    if (!chartCard) return;
    
    const legendItems = chartCard.querySelectorAll('.chart-legend-item');
    
    legendItems.forEach((item, index) => {
        // Start hidden
        item.style.opacity = '0';
        item.style.transform = 'translateY(10px)';
        
        // Animate in with stagger
        setTimeout(() => {
            item.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
            item.style.opacity = '1';
            item.style.transform = 'translateY(0)';
        }, 800 + (index * 150)); // Start after chart animation, stagger by 150ms
    });
}

function createDonutChart(canvas, data, title) {
    const ctx = canvas.getContext('2d');

    // Extract colors from CSS custom property (single source of truth)
    const colorsString = getComputedStyle(document.documentElement).getPropertyValue('--chart-colors').trim();
    const colorPalette = colorsString.split(',').map(color => color.trim());

    new Chart(ctx, {
        type: 'doughnut',
        data: {
            labels: data.labels || [],
            datasets: [{
                data: data.values || [],
                backgroundColor: colorPalette.slice(0, data.values?.length || 0),
                borderWidth: 0,
                borderRadius: 0,
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 1,
            cutout: '60%', // 48px donut width on 240px circle
            plugins: {
                legend: {
                    display: false // We'll create custom legends
                },
                tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    padding: 12,
                    bodyFont: {
                        size: 14
                    },
                    displayColors: true,
                    callbacks: {
                        label: function(context) {
                            const label = context.label || '';
                            const value = context.parsed || 0;
                            return `${label}: ${value}%`;
                        }
                    }
                }
            },
            animation: {
                animateRotate: true,
                animateScale: false,
                duration: 1200,
                easing: 'easeInOutCubic'
            }
        }
    });
}
